import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { debounce } from 'lodash'
import { Trans, useTranslation } from 'react-i18next'

import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import API from '../../../../api/api'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import Loader from '../../../Loader/Loader'

const ImagesDatabase = ({ eventName, handleFieldChange, toggleImageDatabase }) => {
  const { t } = useTranslation(['eventManagement'])
  const { displaySnackbar } = useSnackbar()

  const [query, setQuery] = useState(eventName)
  const [images, setImages] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const shouldDisplayImageGallery = !!query.trim() && images.length > 0 && !isLoading

  const debouncedGetImages = useMemo(() => {
    return debounce(async (query) => {
      if (!query) {
        return
      }

      setIsLoading(true)

      try {
        const resp = await API.getUnsplashImages(query)
        setImages(resp.data.pictures)
      } catch {
        displaySnackbar(t('eventManagement:common.errorLoadingImages'), true)
      } finally {
        setIsLoading(false)
      }
    }, 400)
  }, [t])

  useEffect(() => {
    debouncedGetImages(query)
  }, [query, debouncedGetImages])

  const handleSelectImage = (image) => {
    handleFieldChange({ target: { name: 'image', value: image } })
    toggleImageDatabase(false)
  }

  return (
    <div className="event-form-images-database">
      <div>{t('eventManagement:common.findPerfectImage')}</div>

      <div className="event-form-images-database__header">
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          name="query"
          placeholder={t('eventManagement:common.imageSearchPlaceholder')}
        />
        <button className="button button-gray" onClick={() => toggleImageDatabase(false)}>
          {t('eventManagement:common.closeLibrary')}
        </button>
      </div>
      {isLoading && (
        <div className="event-form-images-database__loader">
          <Loader />
        </div>
      )}
      {shouldDisplayImageGallery && (
        <div className="database-image-wrapper images-container">
          {images.map((image) => (
            <div className="database-image-item" key={image.id}>
              <div role="button" onClick={() => handleSelectImage(image)}>
                <img src={image.url} alt={image.description} />
              </div>
              <p className="image-author">
                <Trans
                  i18nKey="eventManagement:common.photoCredit"
                  components={[
                    <a key="0" href={image.photographerProfile} target="_blank" />,
                    <a key="1" href={image.url} rel="noreferrer" target="_blank" />,
                  ]}
                  values={{ name: image.photographerName }}
                />
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

ImagesDatabase.propTypes = {
  eventName: PropTypes.string,
  handleFieldChange: PropTypes.func,
  toggleImageDatabase: PropTypes.func,
}

export default ImagesDatabase
